/**
 * AdminDashboardPage
 * [UC-001] Admin dashboard with overview sections and navigation to admin features
 * [UC-002] Added logout functionality and recent projects filtering
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { api } from '../core/api-client';
import { Select } from '../components/ui/Select';

export function AdminDashboardPage() {
  const [recentProjects, setRecentProjects] = useState<any[]>([]);
  const [userOptions, setUserOptions] = useState<Array<{id: number, name: string}>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // [UC-002] Logout confirmation modal state
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  // [UC-002] Project filtering state
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedUser, setSelectedUser] = useState('');

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { logout } = useAuth();

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      setError('');

      const [projectsRes, usersRes] = await Promise.all([
        // Pull up to the backend's max page size (100) so the status chart
        // is accurate rather than reading only the default 20. If project
        // count ever exceeds 100, paginate client-side.
        api.get('/projects', { params: { limit: 100 } }).catch(() => ({ items: [] })),
        // Same for users — the per-project "User" column does a client-side
        // {id -> name} lookup, so users on page 2+ rendered as "Unknown".
        api.get('/users', { params: { limit: 100 } }).catch(() => ({ items: [] })),
      ]);

      // Extract items from PaginatedResponse
      const projectItems = (projectsRes as any)?.items || [];
      const userItems = (usersRes as any)?.items || [];

      projectItems.sort((a: any, b: any) => {
        const ad = a.creationDate || a.creation_date || '';
        const bd = b.creationDate || b.creation_date || '';
        return bd.localeCompare(ad);
      });
      setRecentProjects(projectItems);

      if (Array.isArray(userItems)) {
        setUserOptions(userItems.map((u: any) => ({
          id: u.userId || u.user_id,
          name: `${u.name} ${u.surname}`
        })));
      }
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError(t('adminDashboard.loadError', 'Failed to load dashboard data'));
    } finally {
      setIsLoading(false);
    }
  };

  // [UC-002] Logout functionality
  const startLogout = () => {
    setShowLogoutConfirm(true);
  };

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  const confirmLogout = async () => {
    try {
      await logout();
      navigate('/admin/login');
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setShowLogoutConfirm(false);
    }
  };

  // [UC-002] Filter recent projects
  const filteredProjects = recentProjects.filter((project: any) => {
    if (selectedStatus && project.status !== selectedStatus) {
      return false;
    }
    if (selectedUser && project.userId !== parseInt(selectedUser)) {
      return false;
    }
    return true;
  });

  // [UC-002] Dynamic empty message
  const emptyMessage = (() => {
    if (selectedStatus && selectedUser) {
      const userName = userOptions.find(u => u.id === parseInt(selectedUser))?.name || selectedUser;
      return t('adminDashboard.noProjectsStatusUser', { status: selectedStatus, user: userName, defaultValue: `No projects found for status "${selectedStatus}" and user "${userName}"` });
    } else if (selectedStatus) {
      return t('adminDashboard.noProjectsStatus', { status: selectedStatus, defaultValue: `No projects found with status "${selectedStatus}"` });
    } else if (selectedUser) {
      const userName = userOptions.find(u => u.id === parseInt(selectedUser))?.name || selectedUser;
      return t('adminDashboard.noProjectsUser', { user: userName, defaultValue: `No projects found for user "${userName}"` });
    }
    return t('adminDashboard.noProjects', 'No projects are available');
  })();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-[var(--accent-primary)] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[var(--text-muted)]">{t('common.loading', 'Loading...')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={loadDashboardData}
            className="px-4 py-2.5 bg-[var(--accent-primary)] text-[var(--accent-text)] rounded-lg hover:bg-[var(--accent-secondary)] text-sm font-medium transition-colors"
          >
            {t('adminDashboard.retry', 'Retry')}
          </button>
        </div>
      </div>
    );
  }

  // Count projects per status for the breakdown chart. Unknown/empty statuses
  // roll into "Other" so the chart always totals the full fetched set.
  // showRealised flags the statuses where realised value is meaningful: from
  // In development onward — Rejected projects never enter implementation so
  // the column is hidden there.
  const STATUS_CHART_ORDER: { key: string; labelKey: string; fallback: string; color: string; showRealised: boolean }[] = [
    { key: 'AppPreviewPreparation', labelKey: 'adminDashboard.statusAppPreview', fallback: 'App preview preparation', color: 'bg-blue-500',     showRealised: false },
    { key: 'AppPreviewFeedback',    labelKey: 'adminDashboard.statusFeedback',   fallback: 'App preview feedback',    color: 'bg-amber-500',    showRealised: false },
    { key: 'TechnicalAnalysis',     labelKey: 'adminDashboard.statusTechnicalAnalysis', fallback: 'Technical analysis', color: 'bg-cyan-500',    showRealised: false },
    { key: 'SprintPlanning',        labelKey: 'adminDashboard.statusSprintPlanning',    fallback: 'Sprint planning',    color: 'bg-violet-500',  showRealised: false },
    { key: 'InDevelopment',         labelKey: 'adminDashboard.statusInDev',      fallback: 'In development',           color: 'bg-purple-500',  showRealised: true  },
    { key: 'ForTesting',             labelKey: 'adminDashboard.statusForTesting', fallback: 'For testing',              color: 'bg-orange-500',  showRealised: true  },
    { key: 'FinishedTesting',        labelKey: 'adminDashboard.statusFinishedTesting', fallback: 'Finished testing',    color: 'bg-indigo-500', showRealised: true  },
    { key: 'Delivered',              labelKey: 'adminDashboard.statusDelivered',  fallback: 'Delivered',                color: 'bg-emerald-500', showRealised: true  },
    { key: 'Rejected',               labelKey: 'adminDashboard.statusRejected',   fallback: 'Rejected',                 color: 'bg-red-500',     showRealised: false },
  ];
  const num = (v: any): number => {
    if (v == null) return 0;
    const n = typeof v === 'number' ? v : parseFloat(String(v));
    return Number.isFinite(n) ? n : 0;
  };
  const fmtMoney = (v: number) => `€${Math.round(v).toLocaleString()}`;
  const normaliseStatusKey = (raw: string) =>
    raw.replace(/\s+/g, '').replace(/^./, (c: string) => c.toUpperCase());
  const statusCounts: Record<string, number> = {};
  const statusPotentialSums: Record<string, number> = {};
  const statusRealisedSums: Record<string, number> = {};
  for (const p of recentProjects) {
    const key = normaliseStatusKey(p?.status || '');
    statusCounts[key] = (statusCounts[key] || 0) + 1;
    statusPotentialSums[key] = (statusPotentialSums[key] || 0) + num(p.totalPrice);
    statusRealisedSums[key] = (statusRealisedSums[key] || 0) + num(p.realisedPrice);
  }
  const totalProjects = recentProjects.length;
  const maxStatusCount = Math.max(1, ...STATUS_CHART_ORDER.map((s) => statusCounts[s.key] || 0));
  const grandPotential = recentProjects.reduce((sum, p: any) => sum + num(p.totalPrice), 0);
  const grandRealised = recentProjects.reduce((sum, p: any) => sum + num(p.realisedPrice), 0);

  return (
    <div className="max-w-full overflow-x-hidden">
      {/* Header */}
      <div className="mb-5 sm:mb-6">
        <h1 className="text-2xl lg:text-3xl font-bold text-[var(--text-primary)]">{t('adminDashboard.title', 'Admin Dashboard')}</h1>
        <p className="mt-1.5 text-base sm:text-sm text-[var(--text-secondary)]">{t('adminDashboard.subtitle', 'Manage users, projects, and system configuration')}</p>
      </div>

      {/* Projects by status chart — top of the dashboard so the admin sees
          the overall distribution first, before drilling into recent or
          management sections. */}
      <div className="mb-6 sm:mb-8 bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] shadow-sm p-4 sm:p-5 lg:p-6">
        <div className="mb-5">
          <h2 className="text-lg sm:text-xl font-semibold text-[var(--text-primary)]">
            {t('adminDashboard.chart.title', 'Projects by status')}
          </h2>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            {t('adminDashboard.chart.subtitle', 'Live distribution across the project lifecycle')}
          </p>
        </div>
        <div className="space-y-3">
          {/* Column headers — keep aligned with the data rows below. */}
          <div className="hidden sm:flex items-center gap-3 px-1 pb-1 text-[10px] font-medium uppercase tracking-wider text-[var(--text-muted)]">
            <div className="w-56 flex-shrink-0" />
            <div className="flex-1" />
            <div className="w-32 text-right flex-shrink-0">{t('adminDashboard.chart.potential', 'Potential')}</div>
            <div className="w-32 text-right flex-shrink-0">{t('adminDashboard.chart.realised', 'Realised')}</div>
          </div>
          {STATUS_CHART_ORDER.map((s) => {
            const count = statusCounts[s.key] || 0;
            const potential = statusPotentialSums[s.key] || 0;
            const realised = statusRealisedSums[s.key] || 0;
            const pct = maxStatusCount > 0 ? (count / maxStatusCount) * 100 : 0;
            return (
              <div key={s.key} className="flex items-center gap-3">
                <div className="flex items-center gap-2 w-56 flex-shrink-0">
                  <span className={`w-2.5 h-2.5 rounded-full ${s.color} flex-shrink-0`} />
                  <span className="text-xs sm:text-sm text-[var(--text-secondary)] truncate">
                    {t(s.labelKey, s.fallback)}
                  </span>
                  <span className="ml-auto inline-flex items-center justify-center min-w-[28px] px-1.5 py-0.5 rounded-full bg-[var(--surface-raised)] text-xs font-semibold text-[var(--text-primary)] tabular-nums">
                    {count}
                  </span>
                </div>
                <div className="flex-1 h-2 rounded-full bg-[var(--surface-raised)] overflow-hidden">
                  <div
                    className={`h-full rounded-full ${s.color} transition-all`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="hidden sm:block w-32 text-right text-sm font-semibold text-[var(--text-primary)] tabular-nums flex-shrink-0">
                  {fmtMoney(potential)}
                </div>
                <div className="hidden sm:block w-32 text-right text-sm font-semibold tabular-nums flex-shrink-0">
                  {s.showRealised ? (
                    <span className="text-emerald-600 dark:text-emerald-500">{fmtMoney(realised)}</span>
                  ) : (
                    <span className="text-[var(--text-muted)]">—</span>
                  )}
                </div>
              </div>
            );
          })}
          {/* Grand totals row */}
          <div className="flex items-center gap-3 pt-3 mt-2 border-t border-[var(--border-subtle)]">
            <div className="flex items-center gap-2 w-56 flex-shrink-0">
              <span className="text-xs sm:text-sm font-semibold text-[var(--text-primary)] uppercase tracking-wide">
                {t('adminDashboard.chart.total', 'Total')}
              </span>
              <span className="ml-auto inline-flex items-center justify-center min-w-[28px] px-1.5 py-0.5 rounded-full bg-[var(--accent-primary)]/10 text-xs font-bold text-[var(--accent-primary)] tabular-nums">
                {totalProjects}
              </span>
            </div>
            <div className="flex-1" />
            <div className="hidden sm:block w-32 text-right text-sm font-bold text-[var(--text-primary)] tabular-nums flex-shrink-0">
              {fmtMoney(grandPotential)}
            </div>
            <div className="hidden sm:block w-32 text-right text-sm font-bold text-emerald-600 dark:text-emerald-500 tabular-nums flex-shrink-0">
              {fmtMoney(grandRealised)}
            </div>
          </div>
        </div>
      </div>

      {/* [UC-002] Recent Projects Section */}
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <h2 className="text-lg sm:text-xl font-semibold text-[var(--text-primary)]">{t('adminDashboard.recentProjects', 'Recent Projects')}</h2>
          <button
            onClick={() => navigate('/admin/project-list')}
            className="w-full sm:w-auto px-4 py-3 sm:py-2.5 bg-[var(--surface-raised)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--surface-overlay)] border border-[var(--border-subtle)] text-base sm:text-sm font-medium transition-colors"
          >
            {t('adminDashboard.viewAllProjects', 'View All Projects')}
          </button>
        </div>

        {/* Project Filters */}
        <div className="mb-4 p-4 bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)]">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Project Status Filter */}
            <div>
              <Select<string>
                label={t('adminDashboard.projectStatus', 'Project Status')}
                value={selectedStatus}
                onChange={setSelectedStatus}
                options={[
                  { value: '', label: t('adminDashboard.allStatuses', 'All Statuses') },
                  { value: 'AppPreviewPreparation', label: t('adminDashboard.statusAppPreview', 'App preview preparation') },
                  { value: 'Rejected', label: t('adminDashboard.statusRejected', 'Rejected') },
                  { value: 'AppPreviewFeedback', label: t('adminDashboard.statusFeedback', 'App preview feedback') },
                  { value: 'TechnicalAnalysis', label: t('adminDashboard.statusTechnicalAnalysis', 'Technical analysis') },
                  { value: 'SprintPlanning', label: t('adminDashboard.statusSprintPlanning', 'Sprint planning') },
                  { value: 'InDevelopment', label: t('adminDashboard.statusInDev', 'In development') },
                  { value: 'ForTesting', label: t('adminDashboard.statusForTesting', 'For testing') },
                  { value: 'FinishedTesting', label: t('adminDashboard.statusFinishedTesting', 'Finished testing') },
                  { value: 'Delivered', label: t('adminDashboard.statusDelivered', 'Delivered') },
                ]}
              />
            </div>

            {/* Project User Filter */}
            <div>
              <Select<string>
                label={t('adminDashboard.projectUser', 'Project User')}
                value={selectedUser}
                onChange={setSelectedUser}
                options={[
                  { value: '', label: t('adminDashboard.allUsers', 'All Users') },
                  ...userOptions.map((user) => ({ value: user.id.toString(), label: user.name })),
                ]}
              />
            </div>
          </div>
        </div>

        {/* Projects List */}
        {filteredProjects.length > 0 ? (
          <div className="bg-[var(--surface-base)] rounded-lg border border-[var(--border-subtle)] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead className="bg-[var(--surface-raised)]">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wide">{t('adminDashboard.colProjectName', 'Project Name')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wide">{t('adminDashboard.colStatus', 'Status')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wide">{t('adminDashboard.colUser', 'User')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wide">{t('adminDashboard.colCreatedAt', 'Created')}</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-[var(--text-muted)] uppercase tracking-wide">{t('adminDashboard.colPotential', 'Potential')}</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-[var(--text-muted)] uppercase tracking-wide">{t('adminDashboard.colRealised', 'Realised')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wide">{t('adminDashboard.colActions', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border-subtle)]">
                  {filteredProjects.map((project: any) => {
                    const created = project.creationDate || project.creation_date;
                    let createdLabel = '';
                    if (created) {
                      try { createdLabel = new Date(created).toLocaleDateString(); }
                      catch { createdLabel = String(created).slice(0, 10); }
                    }
                    return (
                      <tr key={project.projectId || project.project_id} className="hover:bg-[var(--surface-raised)] transition-colors">
                        <td className="px-4 py-3 text-base sm:text-sm text-[var(--text-primary)] font-medium">{project.name}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2.5 py-1 text-xs font-medium rounded-full border ${
                            project.status === 'InDevelopment' ? 'bg-blue-600/15 text-blue-700 border-blue-600/25' :
                            project.status === 'Delivered' ? 'bg-green-600/15 text-green-700 border-green-600/25' :
                            project.status === 'AppPreviewPreparation' ? 'bg-amber-600/15 text-amber-700 border-amber-600/25' :
                            project.status === 'Rejected' ? 'bg-red-600/15 text-red-700 border-red-600/25' :
                            project.status === 'AppPreviewFeedback' ? 'bg-purple-600/15 text-purple-700 border-purple-600/25' :
                            project.status === 'TechnicalAnalysis' ? 'bg-cyan-600/15 text-cyan-700 border-cyan-600/25' :
                            project.status === 'SprintPlanning' ? 'bg-violet-600/15 text-violet-700 border-violet-600/25' :
                            project.status === 'ForTesting' ? 'bg-blue-600/15 text-blue-700 border-blue-600/25' :
                            project.status === 'FinishedTesting' ? 'bg-indigo-600/15 text-indigo-700 border-indigo-600/25' :
                            'bg-[var(--surface-overlay)] text-[var(--text-secondary)] border-[var(--border-subtle)]'
                          }`}>
                            {project.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-base sm:text-sm text-[var(--text-secondary)]">
                          {userOptions.find(u => u.id === (project.userId || project.user_id))?.name || t('adminDashboard.unknownUser', 'Unknown')}
                        </td>
                        <td className="px-4 py-3 text-sm text-[var(--text-secondary)] tabular-nums whitespace-nowrap">{createdLabel}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-[var(--text-primary)] tabular-nums text-right whitespace-nowrap">{fmtMoney(num(project.totalPrice))}</td>
                        <td className="px-4 py-3 text-sm font-semibold tabular-nums text-right whitespace-nowrap text-emerald-600 dark:text-emerald-500">{fmtMoney(num(project.realisedPrice))}</td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => navigate(`/admin/projects/${project.projectId || project.project_id}`)}
                            className="px-3 py-1.5 text-xs font-medium text-[var(--accent-primary)] hover:bg-[var(--accent-primary)]/10 rounded transition-colors"
                          >
                            {t('adminDashboard.view', 'View')}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="bg-[var(--surface-base)] rounded-lg border border-[var(--border-subtle)] p-8 text-center">
            <p className="text-base sm:text-sm text-[var(--text-muted)]">{emptyMessage}</p>
          </div>
        )}
      </div>

      {/* Quick Actions Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 mb-6 sm:mb-8">
        {/* User Management */}
        <div className="bg-[var(--surface-base)] rounded-lg border border-[var(--border-subtle)] p-4 sm:p-5">
          <h3 className="text-lg sm:text-xl font-semibold text-[var(--text-primary)] mb-3">{t('adminDashboard.userManagement', 'User Management')}</h3>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/admin/user-create')}
              className="w-full px-4 py-3 sm:py-2.5 bg-[var(--accent-primary)] text-[var(--accent-text)] rounded-lg hover:bg-[var(--accent-secondary)] text-base sm:text-sm font-medium transition-colors"
            >
              {t('adminDashboard.createUser', 'Create User')}
            </button>
            <button
              onClick={() => navigate('/admin/user-list')}
              className="w-full px-4 py-3 sm:py-2.5 bg-[var(--surface-raised)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--surface-overlay)] border border-[var(--border-subtle)] text-base sm:text-sm font-medium transition-colors"
            >
              {t('adminDashboard.viewUsers', 'View Users')}
            </button>
          </div>
        </div>

        {/* Configuration */}
        <div className="bg-[var(--surface-base)] rounded-lg border border-[var(--border-subtle)] p-4 sm:p-5">
          <h3 className="text-lg sm:text-xl font-semibold text-[var(--text-primary)] mb-3">{t('adminDashboard.configuration', 'Configuration')}</h3>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/admin/saa-s-pricing')}
              className="w-full px-4 py-3 sm:py-2.5 bg-[var(--surface-raised)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--surface-overlay)] border border-[var(--border-subtle)] text-base sm:text-sm font-medium transition-colors"
            >
              {t('adminDashboard.saasPricing', 'SaaS Pricing')}
            </button>
            <button
              onClick={() => navigate('/admin/credit-packages')}
              className="w-full px-4 py-3 sm:py-2.5 bg-[var(--surface-raised)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--surface-overlay)] border border-[var(--border-subtle)] text-base sm:text-sm font-medium transition-colors"
            >
              {t('adminDashboard.creditPackages', 'Credit Packages')}
            </button>
            <button
              onClick={() => navigate('/admin/timeline-options')}
              className="w-full px-4 py-3 sm:py-2.5 bg-[var(--surface-raised)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--surface-overlay)] border border-[var(--border-subtle)] text-base sm:text-sm font-medium transition-colors"
            >
              {t('adminDashboard.timelineOptions', 'Timeline Options')}
            </button>
          </div>
        </div>

        {/* Integrations */}
        <div className="bg-[var(--surface-base)] rounded-lg border border-[var(--border-subtle)] p-4 sm:p-5">
          <h3 className="text-lg sm:text-xl font-semibold text-[var(--text-primary)] mb-3">{t('adminDashboard.integrations', 'Integrations')}</h3>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/admin/email-config')}
              className="w-full px-4 py-3 sm:py-2.5 bg-[var(--surface-raised)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--surface-overlay)] border border-[var(--border-subtle)] text-base sm:text-sm font-medium transition-colors"
            >
              {t('adminDashboard.configureEmail', 'Configure Email Service')}
            </button>
            <button
              onClick={() => navigate('/admin/stripe-config')}
              className="w-full px-4 py-3 sm:py-2.5 bg-[var(--surface-raised)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--surface-overlay)] border border-[var(--border-subtle)] text-base sm:text-sm font-medium transition-colors"
            >
              {t('adminDashboard.configureStripe', 'Configure Stripe')}
            </button>
          </div>
        </div>

        {/* System */}
        <div className="bg-[var(--surface-base)] rounded-lg border border-[var(--border-subtle)] p-4 sm:p-5">
          <h3 className="text-lg sm:text-xl font-semibold text-[var(--text-primary)] mb-3">{t('adminDashboard.system', 'System')}</h3>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/admin/audit-log')}
              className="w-full px-4 py-3 sm:py-2.5 bg-[var(--surface-raised)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--surface-overlay)] border border-[var(--border-subtle)] text-base sm:text-sm font-medium transition-colors"
            >
              {t('adminDashboard.auditLog', 'Audit Log')}
            </button>
            <button
              onClick={() => navigate('/admin/profile')}
              className="w-full px-4 py-3 sm:py-2.5 bg-[var(--surface-raised)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--surface-overlay)] border border-[var(--border-subtle)] text-base sm:text-sm font-medium transition-colors"
            >
              {t('nav.profile', 'Profile')}
            </button>
            <button
              onClick={() => navigate('/admin/manages')}
              className="w-full px-4 py-3 sm:py-2.5 bg-[var(--surface-raised)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--surface-overlay)] border border-[var(--border-subtle)] text-base sm:text-sm font-medium transition-colors"
            >
              {t('adminDashboard.manageScreens', 'Manage Screens')}
            </button>
          </div>
        </div>

        {/* Financial */}
        <div className="bg-[var(--surface-base)] rounded-lg border border-[var(--border-subtle)] p-4 sm:p-5">
          <h3 className="text-lg sm:text-xl font-semibold text-[var(--text-primary)] mb-3">{t('adminDashboard.financial', 'Financial')}</h3>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/admin/transaction-list')}
              className="w-full px-4 py-3 sm:py-2.5 bg-[var(--surface-raised)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--surface-overlay)] border border-[var(--border-subtle)] text-base sm:text-sm font-medium transition-colors"
            >
              {t('adminDashboard.viewTransactions', 'View Transactions')}
            </button>
            <button
              onClick={() => navigate('/admin/add-use-cases')}
              className="w-full px-4 py-3 sm:py-2.5 bg-[var(--surface-raised)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--surface-overlay)] border border-[var(--border-subtle)] text-base sm:text-sm font-medium transition-colors"
            >
              {t('adminDashboard.useCasePricing', 'Use Case Pricing')}
            </button>
            <button
              onClick={() => navigate('/admin/add-use-cases')}
              className="w-full px-4 py-3 sm:py-2.5 bg-[var(--surface-raised)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--surface-overlay)] border border-[var(--border-subtle)] text-base sm:text-sm font-medium transition-colors"
            >
              {t('adminDashboard.addUseCases', 'Add Use Cases')}
            </button>
          </div>
        </div>

        {/* Projects */}
        <div className="bg-[var(--surface-base)] rounded-lg border border-[var(--border-subtle)] p-4 sm:p-5">
          <h3 className="text-lg sm:text-xl font-semibold text-[var(--text-primary)] mb-3">{t('nav.projects', 'Projects')}</h3>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/admin/project-list')}
              className="w-full px-4 py-3 sm:py-2.5 bg-[var(--surface-raised)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--surface-overlay)] border border-[var(--border-subtle)] text-base sm:text-sm font-medium transition-colors"
            >
              {t('adminDashboard.allProjects', 'All Projects')}
            </button>
          </div>
        </div>
      </div>

      {/* [UC-002] Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-[var(--surface-raised)] rounded-xl border border-[var(--border-subtle)] p-5 sm:p-6 max-w-sm w-full shadow-2xl">
            <h3 className="text-xl font-semibold text-[var(--text-primary)] mb-4">{t('adminDashboard.confirmLogout', 'Confirm Logout')}</h3>
            <p className="text-base sm:text-sm text-[var(--text-secondary)] mb-6">{t('adminDashboard.logoutMessage', 'Are you sure you want to logout? Your session will be terminated.')}</p>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <button
                onClick={cancelLogout}
                className="w-full sm:w-auto px-5 py-3 sm:py-2.5 bg-[var(--surface-raised)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--surface-overlay)] border border-[var(--border-subtle)] text-base sm:text-sm font-medium transition-colors"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={confirmLogout}
                className="w-full sm:w-auto px-5 py-3 sm:py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-base sm:text-sm font-medium transition-colors"
              >
                {t('nav.logout', 'Logout')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboardPage;
